import type { Dispatch, SetStateAction } from 'react';
import {
  type TravelerRef,
  type HotelPlaceOption,
  HOTEL_MAP_AREA_MAX_RADIUS_KM,
  HotelPlannerSelectionSchema,
  MAP_AREA_PLACE_ID,
  type HotelPlannerBlock,
  type HotelPlaceSource,
  type HotelPlannerPlace,
  type HotelPlannerSelection,
  type HotelRoomOccupancy,
} from '@voll-intelligence/genui-contract';

/** Which of the three surfaces the organism shows: the search bar over an empty
 *  map (`search`), the busy skeleton while a search is in flight (`loading`), or
 *  the dock of results (`results`). Pure derivation from the block + busy flag. */
export type HotelStage = 'search' | 'loading' | 'results';

export function deriveStage(ui: HotelPlannerBlock, busy: boolean): HotelStage {
  if (ui.offers === undefined && !busy) return 'search';
  if (busy) return 'loading';
  return 'results';
}

/** A estadia REALMENTE em cena: o refino do preview quando existe, senão o que
 *  o bloco trouxe. O drawer usa pra dividir o total em diária; o pick de hotel
 *  usa pra ECOAR as datas ao agente — sem o eco, o preview refina no cliente e
 *  o FSM continua com as datas velhas.
 *
 *  Os HÓSPEDES viajam junto pelo mesmo motivo, e não só a contagem: refinar
 *  acompanhante no card é leitura local (`searchHotelPreview`), então sem o eco
 *  o agente monta o checkout com a lista de viajantes da busca anterior — o
 *  cabeçalho dizia "2 hóspedes" (isso vinha em `rooms`) e a lista mostrava só o
 *  requester. */
export interface HotelStayEcho {
  checkin?: string;
  checkout?: string;
  rooms?: readonly HotelRoomOccupancy[];
  travelers?: readonly TravelerRef[];
  policyTravelerId?: string;
}

/** Um campo só entra na estadia quando tem conteúdo: lista vazia é ausência de
 *  hóspede, não uma escolha de zero hóspedes. */
function present(value: unknown): boolean {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

export function drawerStay(
  ui: HotelPlannerBlock,
  selection?: HotelPlannerSelection,
): HotelStayEcho {
  const merged = {
    checkin: selection?.checkin ?? ui.checkin,
    checkout: selection?.checkout ?? ui.checkout,
    rooms: selection?.rooms ?? ui.rooms,
    travelers: selection?.travelers ?? ui.travelers,
    policyTravelerId: selection?.policyTravelerId ?? ui.policyTravelerId,
  };
  return Object.fromEntries(Object.entries(merged).filter(([, value]) => present(value)));
}

/** A estadia como o wire aceita: só vira eco quando as DUAS pontas existem —
 *  meia estadia no `stay` faria o FSM mesclar critério pela metade. */
export function staySelection(stay: HotelStayEcho) {
  if (!stay.checkin || !stay.checkout) return undefined;
  return {
    checkin: stay.checkin,
    checkout: stay.checkout,
    ...(stay.rooms ? { rooms: [...stay.rooms] } : {}),
    ...(stay.travelers?.length ? { travelers: [...stay.travelers] } : {}),
    ...(stay.policyTravelerId ? { policyTravelerId: stay.policyTravelerId } : {}),
  };
}

/** A seleção que o BLOCO emitido pelo agente representa (#2857): destino e
 *  estadia como o card os mostra. É o `selection` que o relato do PAINEL de um
 *  card SEM busca client-side manda no `hotel-search-context` — o contexto de
 *  refino a exige, e o agente a mescla sobre slots que já são estes
 *  (idempotente). `undefined` sem destino, sem as duas pontas da estadia ou com
 *  data fora do ISO (o bloco é tolerante; o wire não) — aí o card fica calado,
 *  como antes. */
export function blockSelection(ui: HotelPlannerBlock): HotelPlannerSelection | undefined {
  const stay = staySelection(drawerStay(ui));
  if (!stay) return undefined;
  const parsed = HotelPlannerSelectionSchema.safeParse({
    action: 'search',
    ...(ui.place ? { place: ui.place } : {}),
    ...(ui.placeDetails ? { placeDetails: ui.placeDetails } : {}),
    ...(ui.destinationId === undefined ? {} : { destinationId: ui.destinationId }),
    ...stay,
  });
  return parsed.success ? parsed.data : undefined;
}

/** A seleção da busca na ÁREA DO MAPA (#3114) a partir da busca em cena: a
 *  mesma estadia, ocupação e viajantes, com o destino trocado pela área
 *  visível — o lugar SINTÉTICO (`MAP_AREA_PLACE_ID`, o centro como
 *  coordenada) e o raio medido, no teto do schema. O par da `mapAreaRequest`
 *  da bancada, para o cartão da conversa (#3618), que busca pelo port de
 *  leitura em vez de congelar um pedido. A área é tipada pela FORMA (a fatia
 *  do `MapSearchArea` do kit que importa aqui): este módulo sai na raiz
 *  não-React do pacote, e importar o kit puxaria `.tsx` para consumidores sem
 *  jsx (`trip-document`). */
export function mapAreaSelection(
  base: HotelPlannerSelection,
  area: {
    readonly center: { readonly latitude: number; readonly longitude: number };
    readonly radiusKm: number;
  },
  label: string,
): HotelPlannerSelection {
  return {
    action: 'search',
    place: label,
    placeDetails: {
      placeId: MAP_AREA_PLACE_ID,
      latitude: area.center.latitude,
      longitude: area.center.longitude,
      name: label,
    },
    placeSource: 'map_area',
    searchRadiusKm: Math.min(area.radiusKm, HOTEL_MAP_AREA_MAX_RADIUS_KM),
    checkin: base.checkin,
    checkout: base.checkout,
    ...(base.rooms ? { rooms: base.rooms } : {}),
    ...(base.travelers ? { travelers: base.travelers } : {}),
    ...(base.policyTravelerId ? { policyTravelerId: base.policyTravelerId } : {}),
  };
}

export type SelectionInput = {
  destinationId: number | undefined;
  maxPrice: number | undefined;
  onlyBreakfast: boolean | undefined;
  onlyRefundable: boolean | undefined;
  onlyPolicyWithIn: boolean | undefined;
  place: string;
  placeRef: string | undefined;
  placeDetails: HotelPlannerPlace | undefined;
  placeSource: HotelPlaceSource | undefined;
  checkin: string | undefined;
  checkout: string | undefined;
  rooms: HotelRoomOccupancy[];
  travelers: TravelerRef[];
  policyTravelerId: string | undefined;
};

/** O destino que o bloco já traz, como opção do catálogo do campo — é assim
 *  que "voltar à busca" reabre com o lugar escolhido. A procedência não mora
 *  aqui, e sim no valor da barra (`plannerPlace`). */
export function initialPlace(ui: HotelPlannerBlock): HotelPlaceOption | undefined {
  if (!ui.placeDetails) return undefined;
  return {
    placeId: ui.placeDetails.placeId,
    title: ui.place ?? ui.placeDetails.name ?? ui.placeDetails.city ?? ui.placeDetails.placeId,
    searchPlace: ui.placeDetails,
  };
}

/** Um destino que a PESSOA escolheu fora da lista do campo (o atalho de
 *  recente/favorito): a origem sai, e o `placeId` é escrito mesmo ausente —
 *  um prefill só-texto apaga a coordenada do lugar anterior. */
export function chosenPlace(
  place: string,
  placeId: string | undefined,
): { place: string; selectedPlaceId: string | undefined; placeSource: undefined } {
  return { place, selectedPlaceId: placeId, placeSource: undefined };
}

/** O destino que "Minha localização" resolveu, escrito no valor MAIS NOVO da
 *  faixa — não no que a closure do clique capturou. Genérico no valor (e não
 *  `HotelSearchValue` do kit) porque este arquivo é alcançável pelo barrel puro
 *  do genui-kit: importar o kit aqui arrasta os `.css` dele para o typecheck de
 *  quem só consome schemas (o BFF). A espera embute o prompt de
 *  permissão do navegador, que pode levar segundos, e nesse meio-tempo a pessoa
 *  pode ter mexido em datas, quartos ou hóspedes: um `{...value}` capturado
 *  desfaria tudo isso ao aterrissar. E se ela escolheu OUTRO destino nesse
 *  intervalo, o pedido em voo perdeu a validade — a escolha manual manda, então
 *  a posição é descartada em silêncio em vez de trocar o destino por baixo.
 *  Reescolher o MESMO lugar deixa o valor igual e escapa desta guarda; esse
 *  caso cai antes, no `onPlacePick` da barra, que cancela o pedido
 *  (`useMyLocation`). A origem vai no próprio valor (`placeSource`): o pick
 *  seguinte da barra a apaga, mesmo que a lista devolva o mesmo lugar. */
export function applyMyLocation<
  V extends { place: string; selectedPlaceId?: string; placeSource?: 'geolocation' },
>(
  asked: Pick<V, 'place' | 'selectedPlaceId'>,
  setValue: Dispatch<SetStateAction<V>>,
): (place: HotelPlaceOption | null) => void {
  return (place) => {
    if (!place) return;
    setValue((current) =>
      current.selectedPlaceId === asked.selectedPlaceId && current.place === asked.place
        ? {
            ...current,
            place: place.title,
            selectedPlaceId: place.placeId,
            placeSource: 'geolocation',
          }
        : current,
    );
  };
}

type BlockFilters =
  | 'destinationId'
  | 'maxPrice'
  | 'onlyBreakfast'
  | 'onlyRefundable'
  | 'onlyPolicyWithIn';

/** A seleção de um rascunho já resolvido (`resolveValue` das opções), com os
 *  filtros que o bloco trouxe — `null` enquanto faltar campo obrigatório. */
export function draftSelection(
  ui: Pick<HotelPlannerBlock, BlockFilters>,
  draft: Omit<SelectionInput, BlockFilters>,
): HotelPlannerSelection | null {
  return createSelection({
    destinationId: ui.destinationId,
    maxPrice: ui.maxPrice,
    onlyBreakfast: ui.onlyBreakfast,
    onlyRefundable: ui.onlyRefundable,
    onlyPolicyWithIn: ui.onlyPolicyWithIn,
    ...draft,
  });
}

export function createSelection(input: SelectionInput): HotelPlannerSelection | null {
  if (!input.checkin || !input.checkout || !input.travelers.length || !input.policyTravelerId) {
    return null;
  }
  const place = input.place.trim();
  const parsed = HotelPlannerSelectionSchema.safeParse({
    action: 'search',
    ...(place ? { place } : {}),
    ...(input.placeRef ? { placeRef: input.placeRef } : {}),
    ...(input.placeDetails ? { placeDetails: input.placeDetails } : {}),
    ...(input.placeSource ? { placeSource: input.placeSource } : {}),
    ...(input.destinationId ? { destinationId: input.destinationId } : {}),
    checkin: input.checkin,
    checkout: input.checkout,
    rooms: input.rooms,
    ...(input.maxPrice === undefined ? {} : { maxPrice: input.maxPrice }),
    ...(input.onlyBreakfast === undefined ? {} : { onlyBreakfast: input.onlyBreakfast }),
    ...(input.onlyRefundable === undefined ? {} : { onlyRefundable: input.onlyRefundable }),
    ...(input.onlyPolicyWithIn === undefined ? {} : { onlyPolicyWithIn: input.onlyPolicyWithIn }),
    travelers: input.travelers,
    policyTravelerId: input.policyTravelerId,
  });
  return parsed.success ? parsed.data : null;
}

export function selectionIsSettled(ui: HotelPlannerBlock, dirty: boolean): boolean {
  return !dirty && (ui.offers !== undefined || ui.searchError !== undefined);
}
